import LegalLayout from './LegalLayout';

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy" effectiveDate="May 2, 2026" lastUpdated="May 2, 2026" policyKey="privacy">
      <p>
        Plexura AI Scribe (&ldquo;<strong>Plexura</strong>,&rdquo; &ldquo;<strong>we</strong>,&rdquo;
        &ldquo;<strong>us</strong>,&rdquo; or &ldquo;<strong>our</strong>&rdquo;) operates a
        clinical-documentation platform for licensed mental and behavioral
        health professionals. This Privacy Policy describes how we collect,
        use, disclose, and protect personal information &mdash; including
        Protected Health Information (&ldquo;<strong>PHI</strong>&rdquo;) as defined under the
        Health Insurance Portability and Accountability Act of 1996
        (&ldquo;<strong>HIPAA</strong>&rdquo;) &mdash; when you visit{' '}
        <a href="https://plexura.ai">plexura.ai</a>, install our browser
        extension, or use the Plexura AI Scribe service (collectively, the
        &ldquo;<strong>Service</strong>&rdquo;).
      </p>
      <p>
        This Privacy Policy applies in addition to our{' '}
        <a href="/legal/terms">Terms of Service</a> and{' '}
        <a href="/legal/hipaa">HIPAA Compliance Statement</a>. If you are a
        covered-entity provider (&ldquo;<strong>Covered Entity</strong>&rdquo;) using
        Plexura to create, receive, maintain, or transmit PHI on behalf of
        your patients, our handling of PHI is also governed by the Business
        Associate Agreement (&ldquo;<strong>BAA</strong>&rdquo;) executed between you and Plexura.
        Where this Privacy Policy and the BAA conflict, the BAA controls
        with respect to PHI.
      </p>

      <h2>1. Scope and Roles</h2>
      <p>
        Plexura processes personal information in two distinct capacities:
      </p>
      <ul>
        <li>
          <strong>As a Business Associate.</strong> When you upload session
          audio, generate transcripts, or store clinical notes about your
          patients, you (the clinician or practice) are the Covered Entity
          and Plexura is your Business Associate under 45 C.F.R. §
          160.103. We process PHI only on your documented instructions and
          for the limited purposes set out in your BAA with us.
        </li>
        <li>
          <strong>As a Data Controller.</strong> When you visit our
          marketing site, sign up for an account, request a sales demo, or
          interact with our customer-support channels, Plexura determines
          the purposes and means of processing your information. This
          Privacy Policy describes that processing.
        </li>
      </ul>

      <h2>2. Information We Collect</h2>
      <h3>2.1 Information You Provide Directly</h3>
      <ul>
        <li>
          <strong>Account information</strong> &mdash; full name, email
          address, hashed password (bcrypt with random per-user salt),
          professional role, time zone, organization name, NPI number (if
          submitted), and billing address.
        </li>
        <li>
          <strong>Practice and team metadata</strong> &mdash; practice
          name, member roster, owner identity, invite tokens, EHR template
          selections, and EHR-extension custom configurations.
        </li>
        <li>
          <strong>Patient records you create</strong> &mdash; patient
          identifiers (name, MRN if entered), demographics, session
          metadata, audio recordings (transient), AI-generated transcripts,
          clinical notes (SOAP, psychotherapy progress notes,
          psychiatric evaluations), diagnostic codes (ICD-10, DSM-5), and
          billing codes (CPT, E/M).
        </li>
        <li>
          <strong>Communications</strong> &mdash; emails, support
          tickets, feedback you send to us, and free-text messages
          submitted through &ldquo;Contact Sales&rdquo; or other forms.
        </li>
        <li>
          <strong>Payment information</strong> &mdash; payment-card
          details are submitted directly to Stripe, Inc. We do not store
          full card numbers on our servers; we receive a tokenized
          customer ID, the card brand, and the last four digits for
          display purposes.
        </li>
      </ul>
      <h3>2.2 Information Collected Automatically</h3>
      <ul>
        <li>
          <strong>Device and technical data</strong> &mdash; IP address,
          browser type and version, operating system, device identifiers,
          referring URL, pages visited, session duration, click events,
          and HTTP request headers.
        </li>
        <li>
          <strong>Usage telemetry</strong> &mdash; feature interactions,
          template selections, EHR-push events (date, time, target EHR,
          success/failure), audit-log entries, and error reports.
        </li>
        <li>
          <strong>Cookies and similar technologies</strong> &mdash; we
          use a minimal set of strictly-necessary cookies (session,
          authentication) and a single first-party analytics cookie. We
          do not use third-party advertising trackers, fingerprinting
          libraries, or session-replay tools that capture PHI fields.
        </li>
      </ul>
      <h3>2.3 Information Collected by the Browser Extension</h3>
      <p>
        Our browser extension, when installed and explicitly invoked by
        you, reads the DOM of the EHR tab solely to insert the note text
        into the active note field. It does not transmit EHR DOM content
        back to Plexura beyond the metadata required to confirm a
        successful push (target URL host, field selector key, success or
        error status). The extension never reads, exports, or transmits
        existing chart contents from the EHR.
      </p>

      <h2>3. How We Use Information</h2>
      <p>We use the information we collect for the following purposes:</p>
      <ul>
        <li>
          <strong>Service delivery</strong> &mdash; to authenticate users,
          process audio into transcripts, generate AI-powered clinical
          notes, render previews, push notes to EHR systems via your
          browser extension, and store the resulting records under your
          account.
        </li>
        <li>
          <strong>Account management</strong> &mdash; to create and
          maintain your account, deliver password-reset and magic-link
          emails, manage practice memberships, and process invitations.
        </li>
        <li>
          <strong>Billing and subscription management</strong> &mdash; to
          create Stripe Checkout Sessions, manage recurring subscriptions,
          send invoice receipts and renewal confirmations, and handle
          dunning when payments fail.
        </li>
        <li>
          <strong>Customer support</strong> &mdash; to respond to your
          support requests, troubleshoot errors, and fulfill individual
          rights requests.
        </li>
        <li>
          <strong>Service improvement</strong> &mdash; to analyze
          aggregate, de-identified usage patterns. We do not use PHI
          to train AI models. Any model fine-tuning we perform uses
          only synthetic or fully anonymized data per 45 C.F.R. §
          164.514(b).
        </li>
        <li>
          <strong>Legal compliance and security</strong> &mdash; to
          maintain audit logs as required by HIPAA &sect; 164.312(b),
          detect and investigate fraud or abuse, enforce our Terms of
          Service, and comply with subpoenas, court orders, and
          regulatory requests.
        </li>
      </ul>

      <h2>4. Legal Bases for Processing (EEA / UK Visitors)</h2>
      <p>
        For visitors located in the European Economic Area, the United
        Kingdom, or Switzerland, we rely on the following legal bases under
        the GDPR and UK GDPR:
      </p>
      <ul>
        <li>
          <strong>Performance of a contract</strong> (Art. 6(1)(b)) for
          account creation, subscription billing, and core Service
          delivery.
        </li>
        <li>
          <strong>Legitimate interests</strong> (Art. 6(1)(f)) for
          fraud prevention, security, audit logging, and Service
          analytics, balanced against your interests and rights.
        </li>
        <li>
          <strong>Legal obligation</strong> (Art. 6(1)(c)) for tax
          recordkeeping, breach notification, and lawful production
          requirements.
        </li>
        <li>
          <strong>Consent</strong> (Art. 6(1)(a)) for non-essential
          marketing communications, where required.
        </li>
        <li>
          For special-category health data (Art. 9), we rely on
          Art. 9(2)(h) (provision of health or social care under a
          professional&rsquo;s responsibility) as the operative
          condition, with safeguards described in this policy and the
          BAA.
        </li>
      </ul>

      <h2>5. How We Share Information</h2>
      <p>
        We disclose information only as described below. We do not sell,
        rent, or trade personal information or PHI.
      </p>
      <h3>5.1 Sub-processors</h3>
      <p>
        We rely on a limited set of trusted sub-processors to deliver the
        Service. Each sub-processor with PHI access has executed a HIPAA
        Business Associate Agreement with Plexura.
      </p>
      <ul>
        <li>
          <strong>MongoDB, Inc.</strong> &mdash; managed Atlas database
          (United States). All collections are encrypted at rest with
          AES-256 and in transit with TLS 1.2+. BAA executed.
        </li>
        <li>
          <strong>Stripe, Inc.</strong> &mdash; payment processing,
          subscription management, Billing Portal, invoicing, and
          PCI-DSS scope handling. We share customer email and billing
          address. We do not share PHI with Stripe. BAA on file.
        </li>
        <li>
          <strong>OpenAI, L.L.C.</strong> &mdash; speech-to-text
          (Whisper) and large-language-model inference for
          AI-generated clinical notes. PHI is transmitted in
          encrypted transit only for the purpose of generating your
          note and is not used to train OpenAI&rsquo;s public models
          per Plexura&rsquo;s API agreement and zero-retention BAA terms.
        </li>
        <li>
          <strong>Resend, Inc.</strong> &mdash; transactional email
          (welcome, dunning, password-reset, invoice-receipt). We
          share recipient email, display name, and email body. We do
          not embed PHI in marketing or transactional emails. BAA on
          file.
        </li>
        <li>
          <strong>Emergent Cloud / Kubernetes infrastructure
          provider</strong> &mdash; container hosting, ingress
          load-balancing, and TLS termination. BAA on file.
        </li>
        <li>
          <strong>Google Cloud Platform</strong> (where used by our
          underlying infrastructure provider) &mdash; storage and
          compute. BAA in place via the host provider&rsquo;s
          contractual chain.
        </li>
      </ul>
      <p>
        A current and complete sub-processor list is available upon
        request to{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a>. We
        will provide reasonable advance notice before adding a new
        sub-processor that handles PHI.
      </p>
      <h3>5.2 Within Your Practice</h3>
      <p>
        If you are a member of a practice on the Enterprise plan,
        information you create within that practice (patient records,
        sessions, notes, EHR templates) is visible to other members of the
        same practice in accordance with the role-based permissions your
        practice owner configures. Inviting a clinician adds them to the
        practice&rsquo;s member roster and grants them visibility to
        patient records assigned to them.
      </p>
      <h3>5.3 Legal Disclosures</h3>
      <p>
        We may disclose information when required by law &mdash; for
        example, in response to a subpoena, court order, or other
        compulsory legal process, or where we have a good-faith belief
        that disclosure is necessary to (a) comply with a legal
        obligation, (b) protect the rights, property, or safety of
        Plexura, our users, patients, or the public, or (c) detect,
        prevent, or address fraud, security, or technical issues. Where
        the disclosure is of PHI, we will follow the minimum-necessary
        rule and notify the affected Covered Entity unless prohibited
        from doing so.
      </p>
      <h3>5.4 Business Transfers</h3>
      <p>
        In the event of a merger, acquisition, financing, reorganization,
        bankruptcy, receivership, sale of company assets, or transition
        of service to another provider, your information may be
        transferred to the acquirer subject to the same protections
        described in this Privacy Policy and the BAA. We will notify you
        of any such change and your options.
      </p>

      <h2>6. Data Retention</h2>
      <p>
        We retain different categories of data for different periods:
      </p>
      <ul>
        <li>
          <strong>Audio recordings</strong> &mdash; processed in
          memory for transcription and deleted from disk within
          minutes of upload completion. We do not persist audio
          beyond the time required to produce the transcript.
        </li>
        <li>
          <strong>Transcripts and clinical notes</strong> &mdash;
          retained for the life of your account and for the
          minimum-necessary period required by your applicable
          state-law medical-records retention rule (commonly 6&ndash;10
          years for adults, longer for minors). At account closure,
          you may request export and we will then delete records per
          your written instructions, subject to any legal-hold or
          regulatory-retention obligations.
        </li>
        <li>
          <strong>Account and billing records</strong> &mdash; retained
          for the duration of the contractual relationship plus up to
          seven (7) years for tax and audit purposes.
        </li>
        <li>
          <strong>Audit logs</strong> &mdash; retained for at least
          six (6) years per HIPAA &sect; 164.316(b)(2)(i).
        </li>
        <li>
          <strong>Marketing-form submissions</strong> &mdash; retained
          for up to twenty-four (24) months and then purged unless you
          become a paying customer.
        </li>
      </ul>

      <h2>7. Security</h2>
      <p>
        We maintain administrative, technical, and physical safeguards
        designed to protect personal information and PHI consistent with
        the HIPAA Security Rule (45 C.F.R. Part 164, Subpart C) and
        commercially reasonable industry practice. These include:
      </p>
      <ul>
        <li>TLS 1.2+ encryption for all data in transit.</li>
        <li>AES-256 encryption at rest in MongoDB Atlas.</li>
        <li>
          Bcrypt hashing of all user passwords with a per-user random
          salt; passwords are never stored or logged in plaintext.
        </li>
        <li>
          A HIPAA-aligned password policy: minimum 12 characters, at
          least three of four character classes, blocked common-breach
          stems, and rejected sequential or repeated character runs.
        </li>
        <li>
          JSON Web Tokens (JWT) signed with a 64-character random secret;
          token expiration set to 120 minutes by default.
        </li>
        <li>
          Strict-Transport-Security (HSTS), X-Frame-Options: DENY,
          X-Content-Type-Options: nosniff, and Referrer-Policy headers
          on all responses.
        </li>
        <li>
          A least-privilege CORS allowlist (no wildcard
          credentialed-origin combinations).
        </li>
        <li>
          Role-based access controls (RBAC) at the practice and
          organization level.
        </li>
        <li>
          Rate-limiting and brute-force protection on authentication
          endpoints.
        </li>
        <li>
          Comprehensive audit logging of every PHI access, modification,
          deletion, and export event.
        </li>
        <li>
          Secure software-development lifecycle: dependency-scanning,
          secret-scanning, code review, and pull-request approvals
          before merging any change touching PHI paths.
        </li>
      </ul>
      <p>
        No system is perfectly secure. If you believe your account has
        been compromised, contact us immediately at{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a>.
      </p>

      <h2>8. Your Rights and Choices</h2>
      <p>
        Depending on your jurisdiction, you may have the following rights
        regarding your personal information:
      </p>
      <ul>
        <li>
          <strong>Access</strong> &mdash; request a copy of the personal
          information we hold about you.
        </li>
        <li>
          <strong>Correction</strong> &mdash; ask us to correct inaccurate
          or incomplete information.
        </li>
        <li>
          <strong>Deletion</strong> &mdash; request deletion of your
          personal information, subject to legal-retention obligations.
          Deletion of PHI is governed by the BAA and applicable state law,
          which may require us to return PHI to the Covered Entity rather
          than delete it.
        </li>
        <li>
          <strong>Portability</strong> &mdash; receive a copy of your
          personal data in a structured, commonly used, machine-readable
          format.
        </li>
        <li>
          <strong>Restriction or objection</strong> &mdash; request that we
          limit or stop certain processing.
        </li>
        <li>
          <strong>Withdraw consent</strong> &mdash; where processing
          relies on your consent, withdraw consent at any time without
          affecting the lawfulness of prior processing.
        </li>
        <li>
          <strong>Lodge a complaint</strong> &mdash; with your local data
          protection authority or the U.S. Department of Health &amp;
          Human Services Office for Civil Rights.
        </li>
      </ul>
      <p>
        California residents have additional rights under the CCPA/CPRA,
        including the right to know what categories of personal
        information we collect and the right to direct us not to sell or
        share it. We do not sell or share personal information as those
        terms are defined under the CCPA/CPRA.
      </p>
      <p>
        To exercise any of these rights, email{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a>. We
        will verify your identity before responding and will respond
        within thirty (30) days, or such other period required by law.
      </p>

      <h2>9. Children&rsquo;s Privacy</h2>
      <p>
        The Service is intended for use by licensed clinicians who are at
        least eighteen (18) years old. We do not knowingly collect
        personal information directly from children under thirteen (13).
        Clinical records about minor patients may be created by the
        clinician on the minor&rsquo;s behalf in the course of treatment;
        such records are PHI handled under the BAA, not direct collection
        from children.
      </p>

      <h2>10. International Transfers</h2>
      <p>
        Plexura&rsquo;s primary infrastructure is located in the United
        States. If you access the Service from outside the United States,
        your information will be transferred to, stored, and processed in
        the United States. For transfers from the EEA, UK, or
        Switzerland, we rely on Standard Contractual Clauses approved by
        the European Commission and (for UK transfers) the UK Information
        Commissioner&rsquo;s addendum.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. When we make
        material changes, we will notify you by email (to the address
        associated with your account) and post a banner on the Service at
        least thirty (30) days before the changes take effect. The
        &ldquo;Last updated&rdquo; date at the top of this page reflects
        the most recent revision. Continued use of the Service after the
        effective date constitutes acceptance of the revised Policy.
      </p>

      <h2>12. Contact Us</h2>
      <p>
        Plexura AI Scribe<br />
        Attn: Privacy Officer<br />
        Email:{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a>
      </p>
      <p>
        Use the same address for legal, privacy, individual-rights, HIPAA, and
        security matters. For data-protection matters under EU/UK law, our
        representative contact is the same email address above.
      </p>
    </LegalLayout>
  );
}
